import type { ReactNode, RefObject } from 'react';
import { Children, useRef } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

import type { TreeNavigationDirection } from './useTreeNavigationPopup';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const flyoutTransitionClasses = [
  'flyout-transition-left-to-right',
  'flyout-transition-right-to-left',
  'flyout-transition-up',
] as const;
export type FlyoutTransitionClass = (typeof flyoutTransitionClasses)[number];

export type ResponsiveFlyoutTransitionProps = {
  direction: TreeNavigationDirection;
  transitionKey?: string;
  children: (ref: RefObject<HTMLUListElement | null>) => ReactNode;
};

const transitionDirectionClassMap: Record<TreeNavigationDirection, FlyoutTransitionClass> = {
  leaf: 'flyout-transition-left-to-right',
  root: 'flyout-transition-right-to-left',
  lateral: 'flyout-transition-up',
};

/**
 * Direction-dependent transition to switch between popup contents by sliding in from left, right, or top, depending on the navigation direction in the nav tree.
 *
 * Parent to child: slide out old items to the left, and new items in from the right
 * Child to parent: slide out old items to the right, and new items in from the left
 * Sibling or different main item: slide out old items to the top and new items in from the top
 */
export const ResponsiveFlyoutTransition = ({
  direction,
  transitionKey,
  children,
}: ResponsiveFlyoutTransitionProps) => {
  const ref = useRef<HTMLUListElement>(null);

  return (
    <div
      // NOTE: transition styles are applied to the parent element with class selectors because SwitchTransition does not support reactive styles based on the direction like we need here
      className={transitionDirectionClassMap[direction]}
    >
      <SwitchTransition mode="out-in">
        <CSSTransition
          appear
          timeout={125}
          key={transitionKey ?? 'nothing'} // fallback key is needed due to https://github.com/reactjs/react-transition-group/issues/866
          classNames="flyout-transition"
          nodeRef={ref}
        >
          {Children.only(children(ref))}
        </CSSTransition>
      </SwitchTransition>
    </div>
  );
};
